import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import { 
  Image, 
  MessageSquareQuote, 
  Newspaper, 
  FolderTree, 
  Plus, 
  Pencil, 
  Archive,
  X, 
  Star, 
  Check,
  BarChart3,
  Eye,
  EyeOff,
  FileText,
  Settings,
  Layers,
  Globe,
  Zap,
  Clock,
  Timer,
  Users,
  Phone,
  Mail,
  MapPin,
  Building,
  IndianRupee,
  MessageCircle,
  UserCheck,
  Edit3,
  Link,
  Upload,
  ShieldCheck,
  History,
  RotateCcw,
  Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'dashboard', name: 'Dashboard', icon: BarChart3 },
  { id: 'homepage', name: 'Homepage', icon: Globe },
  { id: 'logo-bank', name: 'Logo Bank', icon: Building },
  { id: 'banners', name: 'Banners', icon: Image },
  { id: 'flash-sales', name: 'Flash Sales', icon: Zap },
  { id: 'testimonials', name: 'Testimonials', icon: MessageSquareQuote },
  { id: 'news', name: 'News', icon: Newspaper },
  { id: 'categories', name: 'Categories', icon: FolderTree },
  { id: 'page-content', name: 'Page Content', icon: Layers },
  { id: 'static-pages', name: 'Static Pages', icon: FileText },
  { id: 'settings', name: 'Site Settings', icon: Settings },
  { id: 'approvals', name: 'Approvals', icon: ShieldCheck },
  { id: 'audit', name: 'Audit', icon: History },
];

const BANNER_POSITIONS = [
  { value: 'home_top', label: 'Homepage Top' },
  { value: 'home_middle', label: 'Homepage Middle' },
  { value: 'home_bottom', label: 'Homepage Bottom' },
  { value: 'category_top', label: 'Category Page Top' },
  { value: 'product_sidebar', label: 'Product Sidebar' },
  { value: 'buy_mobile_hero', label: 'Buy Mobile - Hero Slider' },
  { value: 'buy_laptop_hero', label: 'Buy Laptop - Hero Slider' },
];

const BANNER_TYPES = [
  { value: 'hero', label: 'Hero Slider' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'category', label: 'Category' },
  { value: 'mobile', label: 'Mobile Only' },
  { value: 'sidebar', label: 'Sidebar' },
];

const CONTENT_STATUS = [
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const NEWS_CATEGORIES = [
  { value: 'press', label: 'Press Release' },
  { value: 'media', label: 'Media Coverage' },
  { value: 'blog', label: 'Blog Post' },
  { value: 'announcement', label: 'Announcement' },
];

const PAGE_SECTION_TYPES = [
  { value: 'usp', label: 'USP (Unique Selling Points)' },
  { value: 'feature', label: 'Feature Section' },
  { value: 'stat', label: 'Statistics' },
  { value: 'faq', label: 'FAQ' },
  { value: 'team', label: 'Team Member' },
  { value: 'partner', label: 'Partner Logo' },
  { value: 'brand', label: 'Brand Logo' },
  { value: 'social', label: 'Social Links' },
  { value: 'custom', label: 'Custom Block' },
  { value: 'assurance', label: 'Zobox Assurance (Buy Pages)' },
  { value: 'grade', label: 'Grades Explained (Buy Pages)' },
  { value: 'process', label: 'Zobox Process Steps (Buy Pages)' },
  { value: 'hero', label: 'Hero Section' },
  { value: 'partner_step', label: 'Partner Onboarding Step' },
  { value: 'cta', label: 'Call-to-Action Section' },
  { value: 'benefit', label: 'Benefit/Advantage' },
  { value: 'testimonial_highlight', label: 'Featured Testimonial' },
];

const PAGE_SLUGS = [
  { value: 'home', label: 'Homepage' },
  { value: 'about', label: 'About Us' },
  { value: 'why-refurbished', label: 'Why Refurbished' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'contact', label: 'Contact' },
  { value: 'buy-mobile', label: 'Buy Mobile Page' },
  { value: 'buy-laptop', label: 'Buy Laptop Page' },
  { value: 'wholesaler', label: 'Wholesaler Page' },
  { value: 'refurbished', label: 'Refurbished Explainer Page' },
  { value: 'channel-partner', label: 'Channel Partner Page' },
];

const HOMEPAGE_SECTIONS = [
  { id: 'buy', name: 'Buy Devices', order: 10, title: 'Find your next device', enabled: true },
  { id: 'promo_a', name: 'Campaign Banner Group A', order: 20, title: 'Campaign banner group A', enabled: true },
  { id: 'mobile_deals', name: 'Mobile Deals', order: 30, title: 'Mobile deals', enabled: true },
  { id: 'promo_b', name: 'Campaign Banner Group B', order: 40, title: 'Campaign banner group B', enabled: true },
  { id: 'laptop_deals', name: 'Laptop Deals', order: 50, title: 'Laptop deals', enabled: true },
  { id: 'applications', name: 'ZOBOX Applications', order: 60, title: 'The ZOBOX ecosystem in your pocket', enabled: true },
  { id: 'promo_c', name: 'Campaign Banner Group C', order: 70, title: 'Campaign banner group C', enabled: true },
  { id: 'brands', name: 'Device Brands', order: 80, title: 'Explore leading brands', enabled: true },
  { id: 'stores', name: 'ZOBOX Stores', order: 90, title: 'Visit a ZOBOX Store', enabled: true },
  { id: 'articles_media', name: 'Articles & Media', order: 100, title: 'ZOBOX stories and updates', enabled: true },
  { id: 'sell', name: 'Sell to ZOBOX', order: 110, title: 'Sell your device', enabled: false },
  { id: 'assured', name: 'ZOBOX Assured', order: 120, title: 'Confidence before and after you buy', enabled: false },
];

const WEBSITE_PREVIEW_URL = process.env.REACT_APP_PUBLIC_WEBSITE_PREVIEW_URL
  || 'https://zobox-website-staging-6cgwjq3laq-as.a.run.app';

function B2CCMS({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Data states
  const [banners, setBanners] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageContent, setPageContent] = useState([]);
  const [staticPages, setStaticPages] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandCategory, setBrandCategory] = useState('Mobile Phones');
  const [cmsPermissions, setCmsPermissions] = useState({
    can_view: false,
    can_edit: false,
    can_approve: false,
    can_override: false,
  });
  const [approvals, setApprovals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [versions, setVersions] = useState([]);
  const [versionTarget, setVersionTarget] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchCmsPermissions();
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchStats();
    } else if (activeTab === 'homepage') {
      fetchPageContent();
      fetchBanners();
    } else if (activeTab === 'logo-bank') {
      fetchBrands();
    } else if (activeTab === 'banners') {
      fetchBanners();
    } else if (activeTab === 'flash-sales') {
      fetchFlashSales();
    } else if (activeTab === 'testimonials') {
      fetchTestimonials();
    } else if (activeTab === 'news') {
      fetchNews();
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'page-content') {
      fetchPageContent();
    } else if (activeTab === 'static-pages') {
      fetchStaticPages();
    } else if (activeTab === 'settings') {
      fetchSiteSettings();
    } else if (activeTab === 'approvals') {
      fetchApprovals();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab, brandCategory]);

  const fetchCmsPermissions = async () => {
    try {
      const res = await axios.get(`${API}/api/b2c/cms/workflow/permissions`, getAuthHeaders());
      setCmsPermissions(res.data || {});
    } catch (error) {
      console.error('Error fetching CMS permissions:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/b2c/cms/dashboard/stats`, getAuthHeaders());
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/brands?category=${brandCategory}`, getAuthHeaders());
      setBrands(res.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  // State for manual logo URL input
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [manualLogoUrl, setManualLogoUrl] = useState('');

  const handleManualLogoSubmit = async (brandId) => {
    if (!manualLogoUrl.trim()) {
      alert('Please enter a logo URL');
      return;
    }
    try {
      await axios.patch(`${API}/api/brands/${brandId}`, { 
        logo_url: manualLogoUrl.trim(),
        logo_source: 'manual'
      }, getAuthHeaders());
      fetchBrands();
      setEditingBrandId(null);
      setManualLogoUrl('');
      alert('Brand logo updated successfully!');
    } catch (error) {
      console.error('Error updating brand logo:', error);
      alert('Failed to update brand logo');
    }
  };

  const handleUpdateBrandLogo = async (brandId, logoUrl) => {
    try {
      await axios.patch(`${API}/api/brands/${brandId}`, { logo_url: logoUrl }, getAuthHeaders());
      fetchBrands();
      alert('Brand logo updated successfully!');
    } catch (error) {
      console.error('Error updating brand logo:', error);
      alert('Failed to update brand logo');
    }
  };

  const handleRefreshAllLogos = async () => {
    if (!window.confirm('This will refresh logos for all brands. Continue?')) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/brands/refresh-all-logos`, {}, getAuthHeaders());
      alert(`✅ Updated: ${res.data.updated}\n❌ Failed: ${res.data.failed}\n⏭️ Skipped: ${res.data.skipped}`);
      fetchBrands();
    } catch (error) {
      console.error('Error refreshing logos:', error);
      alert('Failed to refresh logos');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/banners`, getAuthHeaders());
      setBanners(res.data.banners || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlashSales = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/flash-sales`, getAuthHeaders());
      setFlashSales(res.data.flash_sales || []);
    } catch (error) {
      console.error('Error fetching flash sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/testimonials`, getAuthHeaders());
      setTestimonials(res.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/news`, getAuthHeaders());
      setNews(res.data.news || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/categories`, getAuthHeaders());
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageContent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/page-content`, getAuthHeaders());
      setPageContent(res.data.content || []);
    } catch (error) {
      console.error('Error fetching page content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaticPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/static-pages`, getAuthHeaders());
      setStaticPages(res.data.pages || []);
    } catch (error) {
      console.error('Error fetching static pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/site-settings/manage`, getAuthHeaders());
      setSiteSettings(res.data || {});
    } catch (error) {
      console.error('Error fetching site settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/approvals`, getAuthHeaders());
      setApprovals(res.data.approvals || []);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2c/cms/audit-logs`, getAuthHeaders());
      setAuditLogs(res.data.audit_logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = (type) => {
    setModalType(type);
    setEditItem(null);
    if (type === 'testimonial') {
      setFormData({ rating: 5 });
    } else if (type === 'flash-sale') {
      // Set default times for flash sale
      const now = new Date();
      const endTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
      setFormData({
        start_time: now.toISOString().slice(0, 16),
        end_time: endTime.toISOString().slice(0, 16),
        discount_type: 'percentage',
        discount_value: 10,
        is_active: true,
        page_slug: 'buy-mobile'
      });
    } else {
      setFormData({});
    }
    setShowModal(true);
  };

  const openEditModal = (type, item) => {
    setModalType(type);
    setEditItem(item);
    // For flash sales, convert ISO dates to local datetime format for input
    if (type === 'flash-sale' && item) {
      setFormData({
        ...item,
        start_time: item.start_time ? item.start_time.slice(0, 16) : '',
        end_time: item.end_time ? item.end_time.slice(0, 16) : ''
      });
    } else {
      setFormData({
        ...item,
        start_date: item?.start_date ? item.start_date.slice(0, 16) : '',
        end_date: item?.end_date ? item.end_date.slice(0, 16) : ''
      });
    }
    setShowModal(true);
  };

  const openHomepageSection = (definition, existing) => {
    if (existing) {
      openEditModal('page-content', existing);
      return;
    }
    setModalType('page-content');
    setEditItem(null);
    setFormData({
      page_slug: 'home',
      section_type: 'custom',
      title: definition.title,
      display_order: definition.order,
      is_active: definition.enabled,
      metadata: {
        kind: 'homepage_section',
        section_id: definition.id,
        enabled: definition.enabled,
      },
    });
    setShowModal(true);
  };

  const updateHomepageSection = async (item, changes) => {
    const metadata = {
      ...(item.metadata || {}),
      kind: 'homepage_section',
      section_id: item.metadata?.section_id,
      ...(changes.metadata || {}),
    };
    await axios.put(
      `${API}/api/b2c/cms/page-content/${item.id}`,
      { ...changes, metadata },
      getAuthHeaders()
    );
  };

  const handleHomepageVisibility = async (item) => {
    try {
      const enabled = !(item.metadata?.enabled ?? item.is_active);
      await updateHomepageSection(item, { is_active: enabled, metadata: { enabled } });
      await fetchPageContent();
    } catch (error) {
      console.error('Error updating homepage visibility:', error);
      alert(error.response?.data?.detail || 'Unable to update homepage visibility');
    }
  };

  const handleHomepageMove = async (items, index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const current = items[index];
    const adjacent = items[nextIndex];
    try {
      await Promise.all([
        updateHomepageSection(current, { display_order: adjacent.display_order }),
        updateHomepageSection(adjacent, { display_order: current.display_order }),
      ]);
      await fetchPageContent();
    } catch (error) {
      console.error('Error reordering homepage:', error);
      alert(error.response?.data?.detail || 'Unable to reorder homepage sections');
    }
  };

  const handleHomepagePreview = async () => {
    try {
      const response = await axios.post(
        `${API}/api/b2c/cms/preview/homepage-token`,
        {},
        getAuthHeaders()
      );
      const previewUrl = new URL(WEBSITE_PREVIEW_URL);
      previewUrl.searchParams.set('cms_preview', response.data.token);
      window.open(previewUrl.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening homepage preview:', error);
      alert(error.response?.data?.detail || 'Draft preview is not configured yet');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoints = {
        banner: 'banners',
        'flash-sale': 'flash-sales',
        testimonial: 'testimonials',
        news: 'news',
        category: 'categories',
        'page-content': 'page-content',
        'static-page': 'static-pages'
      };

      const endpoint = endpoints[modalType];
      
      // For flash sales, convert datetime-local to ISO format
      let submitData = { ...formData };
      if (modalType === 'flash-sale') {
        if (submitData.start_time) {
          submitData.start_time = new Date(submitData.start_time).toISOString();
        }
        if (submitData.end_time) {
          submitData.end_time = new Date(submitData.end_time).toISOString();
        }
      }
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      }
      if (submitData.end_date) {
        submitData.end_date = new Date(submitData.end_date).toISOString();
      }
      if (['published', 'scheduled'].includes(submitData.status)) {
        delete submitData.status;
      }
      
      if (editItem) {
        await axios.put(`${API}/api/b2c/cms/${endpoint}/${editItem.id}`, submitData, getAuthHeaders());
      } else {
        await axios.post(`${API}/api/b2c/cms/${endpoint}`, submitData, getAuthHeaders());
      }

      setShowModal(false);
      
      // Refresh the data
      if (modalType === 'banner') fetchBanners();
      else if (modalType === 'flash-sale') fetchFlashSales();
      else if (modalType === 'testimonial') fetchTestimonials();
      else if (modalType === 'news') fetchNews();
      else if (modalType === 'category') fetchCategories();
      else if (modalType === 'page-content') fetchPageContent();
      else if (modalType === 'static-page') fetchStaticPages();
    } catch (error) {
      console.error('Error saving:', error);
      alert(error.response?.data?.detail || 'Error saving data');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (type, id) => {
    if (!window.confirm('Archive this item? It will be removed from the live website but retained in history.')) return;
    
    try {
      const endpoints = {
        banner: 'banners',
        'flash-sale': 'flash-sales',
        testimonial: 'testimonials',
        news: 'news',
        category: 'categories',
        'page-content': 'page-content',
        'static-page': 'static-pages'
      };
      
      await axios.delete(`${API}/api/b2c/cms/${endpoints[type]}/${id}`, getAuthHeaders());
      
      if (type === 'banner') fetchBanners();
      else if (type === 'flash-sale') fetchFlashSales();
      else if (type === 'testimonial') fetchTestimonials();
      else if (type === 'news') fetchNews();
      else if (type === 'category') fetchCategories();
      else if (type === 'page-content') fetchPageContent();
      else if (type === 'static-page') fetchStaticPages();
    } catch (error) {
      console.error('Error archiving:', error);
      alert(error.response?.data?.detail || 'Error archiving');
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/b2c/cms/site-settings`, siteSettings, getAuthHeaders());
      alert('Settings draft saved successfully');
      fetchSiteSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const refreshContentType = (type) => {
    if (type === 'banners') fetchBanners();
    else if (type === 'flash-sales') fetchFlashSales();
    else if (type === 'testimonials') fetchTestimonials();
    else if (type === 'news') fetchNews();
    else if (type === 'categories') fetchCategories();
    else if (type === 'page-content') fetchPageContent();
    else if (type === 'static-pages') fetchStaticPages();
    else if (type === 'site-settings') fetchSiteSettings();
    fetchStats();
  };

  const handleSubmitForApproval = async (type, id, requestedStatus = 'published') => {
    try {
      await axios.post(
        `${API}/api/b2c/cms/${type}/${id}/submit`,
        { requested_status: requestedStatus },
        getAuthHeaders()
      );
      refreshContentType(type);
      alert('Submitted for approval');
    } catch (error) {
      console.error('Error submitting for approval:', error);
      alert(error.response?.data?.detail || 'Unable to submit for approval');
    }
  };

  const handleApprovalDecision = async (approvalId, action) => {
    try {
      await axios.post(
        `${API}/api/b2c/cms/approvals/${approvalId}/decision`,
        { action },
        getAuthHeaders()
      );
      fetchApprovals();
      fetchStats();
      alert(`Approval ${action === 'approve' ? 'approved' : 'rejected'}`);
    } catch (error) {
      console.error('Error deciding approval:', error);
      alert(error.response?.data?.detail || 'Unable to update approval');
    }
  };

  const handleEmergencyOverride = async (type, id) => {
    const reason = window.prompt('Emergency override reason (minimum 10 characters):');
    if (!reason) return;
    if (reason.trim().length < 10) {
      alert('Emergency override requires a reason of at least 10 characters.');
      return;
    }
    if (!window.confirm('Publish this draft immediately using the audited Super Admin override?')) return;
    try {
      await axios.post(
        `${API}/api/b2c/cms/${type}/${id}/override`,
        { target_status: 'published', reason: reason.trim() },
        getAuthHeaders()
      );
      refreshContentType(type);
      fetchAuditLogs();
      alert('Emergency override applied and recorded in the audit log.');
    } catch (error) {
      console.error('Error applying emergency override:', error);
      alert(error.response?.data?.detail || 'Unable to apply emergency override');
    }
  };

  const openVersions = async (type, id) => {
    try {
      const res = await axios.get(`${API}/api/b2c/cms/${type}/${id}/versions`, getAuthHeaders());
      setVersionTarget({ type, id });
      setVersions(res.data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
      alert(error.response?.data?.detail || 'Unable to load versions');
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    if (!versionTarget) return;
    if (!window.confirm(`Restore version ${versionNumber} as a draft?`)) return;
    try {
      await axios.post(
        `${API}/api/b2c/cms/${versionTarget.type}/${versionTarget.id}/restore/${versionNumber}`,
        {},
        getAuthHeaders()
      );
      refreshContentType(versionTarget.type);
      setVersionTarget(null);
      setVersions([]);
      alert('Version restored as draft');
    } catch (error) {
      console.error('Error restoring version:', error);
      alert(error.response?.data?.detail || 'Unable to restore version');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-gray-100 text-gray-600',
      scheduled: 'bg-blue-100 text-blue-700',
      archived: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.draft}`}>
        {status}
      </span>
    );
  };

  const getApprovalBadge = (approvalStatus) => {
    const status = approvalStatus || 'draft';
    const colors = {
      draft: 'bg-gray-100 text-gray-600',
      pending_approval: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.draft}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const renderWorkflowActions = (type, item) => (
    <div className="flex flex-wrap items-center gap-2">
      {getApprovalBadge(item.approval_status)}
      {item.current_version && (
        <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">v{item.current_version}</span>
      )}
      {cmsPermissions.can_edit && item.approval_status !== 'pending_approval' && item.status !== 'archived' && (
        <button
          onClick={() => handleSubmitForApproval(type, item.id)}
          className="px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg flex items-center gap-1"
        >
          <Send className="w-3.5 h-3.5" /> Submit
        </button>
      )}
      <button
        onClick={() => openVersions(type, item.id)}
        className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
      >
        <History className="w-3.5 h-3.5" /> Versions
      </button>
      {cmsPermissions.can_override && item.status !== 'archived' && (
        <button
          onClick={() => handleEmergencyOverride(type, item.id)}
          className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded-lg flex items-center gap-1"
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Override
        </button>
      )}
    </div>
  );

  const renderScheduleFields = () => {
    const scheduledTypes = ['banner', 'testimonial', 'news', 'category', 'page-content', 'static-page'];
    if (!scheduledTypes.includes(modalType)) return null;
    return (
      <div className="grid grid-cols-2 gap-4 border-t pt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Publish From</label>
          <input
            type="datetime-local"
            value={formData.start_date || ''}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expire At</label>
          <input
            type="datetime-local"
            value={formData.end_date || ''}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
    );
  };

  const renderApprovals = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Approval Queue</h2>
          <p className="text-gray-500 text-sm">Review submitted website content</p>
        </div>
        <button onClick={fetchApprovals} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
          Refresh
        </button>
      </div>
      <div className="grid gap-4">
        {approvals.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center text-gray-500">No approval requests</div>
        ) : approvals.map(approval => (
          <div key={approval.id} className="bg-white rounded-xl border p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{approval.snapshot?.title || approval.snapshot?.name || approval.content_id}</h3>
                  {getApprovalBadge(approval.status === 'pending' ? 'pending_approval' : approval.status)}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span>{approval.content_type.replace('_', ' ')}</span>
                  <span>v{approval.version_number}</span>
                  <span>{formatDateTime(approval.submitted_at)}</span>
                  <span>{approval.requested_status}</span>
                </div>
                {approval.note && <p className="text-sm text-gray-600 mt-2">{approval.note}</p>}
              </div>
              {cmsPermissions.can_approve && approval.status === 'pending' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApprovalDecision(approval.id, 'approve')}
                    className="px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 text-green-700 rounded-lg flex items-center gap-1"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    onClick={() => handleApprovalDecision(approval.id, 'reject')}
                    className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded-lg flex items-center gap-1"
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAuditLogs = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Audit Log</h2>
          <p className="text-gray-500 text-sm">CMS change history</p>
        </div>
        <button onClick={fetchAuditLogs} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
          Refresh
        </button>
      </div>
      <div className="bg-white rounded-xl border divide-y">
        {auditLogs.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No audit events</div>
        ) : auditLogs.map(log => (
          <div key={log.id} className="p-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-medium text-gray-900">{log.action.replaceAll('_', ' ')}</p>
              <p className="text-sm text-gray-500">{log.content_type?.replace('_', ' ')} · {log.content_id}</p>
            </div>
            <div className="text-right text-xs text-gray-500">
              <p>{formatDateTime(log.timestamp)}</p>
              <p>{log.actor_role || log.actor_id}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderVersionsModal = () => {
    if (!versionTarget) return null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-xl font-semibold">Version History</h2>
            <button onClick={() => setVersionTarget(null)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-90px)] space-y-3">
            {versions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No versions found</div>
            ) : versions.map(version => (
              <div key={version.id} className="border rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Version {version.version_number}</p>
                  <p className="text-sm text-gray-500">{version.change_type.replaceAll('_', ' ')} · {formatDateTime(version.created_at)}</p>
                </div>
                {cmsPermissions.can_edit && (
                  <button
                    onClick={() => handleRestoreVersion(version.version_number)}
                    className="px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg flex items-center gap-1"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats && Object.entries(stats).map(([key, value]) => (
          <div key={key} className="bg-white rounded-xl border p-5">
            <h4 className="text-sm font-medium text-gray-500 capitalize">{key.replace('_', ' ')}</h4>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value.total || 0}</p>
            <div className="mt-2 text-xs text-gray-500">
              {value.published !== undefined && <span className="text-green-600">{value.published} published</span>}
              {value.active !== undefined && <span className="text-green-600">{value.active} active</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href={WEBSITE_PREVIEW_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
            <Globe className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">View Website</span>
          </a>
          <button onClick={() => setActiveTab('banners')} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left">
            <Image className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Manage Banners</span>
          </button>
          <button onClick={() => setActiveTab('page-content')} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left">
            <Layers className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Page Content</span>
          </button>
          <button onClick={() => setActiveTab('settings')} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left">
            <Settings className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Site Settings</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Render Logo Bank
  const renderLogoBank = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Brand Logo Bank</h2>
          <p className="text-gray-500 text-sm">Manage brand logos for use across applications and websites</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={brandCategory}
            onChange={(e) => setBrandCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="Mobile Phones">Mobile Phones</option>
            <option value="Laptops">Laptops</option>
            <option value="Tablets">Tablets</option>
            <option value="Wearables">Wearables</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm text-gray-500">Total Brands</p>
          <p className="text-2xl font-bold text-gray-900">{brands.length}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm text-gray-500">With Logos</p>
          <p className="text-2xl font-bold text-green-600">{brands.filter(b => b.logo_url).length}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm text-gray-500">Missing Logos</p>
          <p className="text-2xl font-bold text-amber-600">{brands.filter(b => !b.logo_url).length}</p>
        </div>
        <div className="bg-white rounded-xl border p-4 flex flex-col justify-center">
          <button
            onClick={handleRefreshAllLogos}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? 'Refreshing...' : 'Refresh All Logos'}
          </button>
        </div>
      </div>

      {/* Brand Grid */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Brand Logos - {brandCategory}</h3>
          <span className="text-sm text-gray-500">{brands.length} brands</span>
        </div>
        
        {brands.length === 0 ? (
          <div className="p-12 text-center">
            <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No brands found</h3>
            <p className="text-gray-500 mt-1">Brands will appear here when products are scraped</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
            {brands.map(brand => (
              <div key={brand.id} className="bg-gray-50 rounded-xl p-4 hover:shadow-md transition-shadow">
                {/* Logo Display Area */}
                <div className="aspect-square bg-white rounded-lg flex items-center justify-center mb-3 border-2 border-dashed border-gray-200 relative group overflow-hidden">
                  {brand.logo_url ? (
                    <>
                      <img 
                        src={brand.logo_url} 
                        alt={brand.name}
                        className="max-w-[80%] max-h-[80%] object-contain"
                        onError={(e) => {
                          const brandName = brand.name?.toLowerCase().replace(/\s+/g, '');
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${brand.name}&background=2b3380&color=fff&size=128`;
                        }}
                      />
                    </>
                  ) : (
                    <div className="text-center">
                      <Building className="w-12 h-12 text-gray-300 mx-auto" />
                      <p className="text-xs text-gray-400 mt-2">No Logo</p>
                    </div>
                  )}
                </div>

                {/* Brand Name */}
                <div className="text-center mb-3">
                  <p className="font-semibold text-gray-900 truncate">{brand.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {brand.logo_url ? (
                      <span className="text-green-600">✓ {brand.logo_source || 'Logo Added'}</span>
                    ) : (
                      <span className="text-amber-600">⚠ No Logo</span>
                    )}
                  </p>
                </div>

                {/* Manual URL Input (shown when editing) */}
                {editingBrandId === brand.id ? (
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={manualLogoUrl}
                      onChange={(e) => setManualLogoUrl(e.target.value)}
                      placeholder="Enter logo URL..."
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleManualLogoSubmit(brand.id)}
                        className="flex-1 px-2 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingBrandId(null); setManualLogoUrl(''); }}
                        className="flex-1 px-2 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Action Buttons */
                  <div className="space-y-2">
                    <button
                      onClick={() => { setEditingBrandId(brand.id); setManualLogoUrl(brand.logo_url || ''); }}
                      className="w-full px-3 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 flex items-center justify-center gap-1"
                    >
                      <Edit3 className="w-3 h-3" />
                      {brand.logo_url ? 'Edit URL' : 'Add URL'}
                    </button>
                    <ImageUploader
                      onUpload={(url) => handleUpdateBrandLogo(brand.id, url)}
                      buttonText={brand.logo_url ? "Upload New" : "Upload Image"}
                      buttonClassName="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 flex items-center justify-center gap-1"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Info */}
      <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Where Brand Logos Are Used</p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• <strong>Product Pages</strong> - Displayed next to brand name</li>
              <li>• <strong>Product Listings</strong> - Brand filter tabs</li>
              <li>• <strong>Sell Phone Flow</strong> - Brand selection step</li>
              <li>• <strong>B2C Website</strong> - Featured brands section</li>
              <li>• <strong>Partner Portal</strong> - Brand catalog</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Banners
  const renderBanners = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Banners</h2>
          <p className="text-gray-500 text-sm">Manage homepage and promotional banners</p>
        </div>
        <button
          onClick={() => openAddModal('banner')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-banner-btn"
        >
          <Plus className="w-4 h-4" />
          Add Banner
        </button>
      </div>

      <div className="grid gap-4">
        {banners.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No banners yet</h3>
            <p className="text-gray-500 mt-1">Add your first banner to display on the website</p>
          </div>
        ) : (
          banners.map(banner => (
            <div key={banner.id} className="bg-white rounded-xl border p-4 flex gap-4" data-testid={`banner-item-${banner.id}`}>
              <div className="w-48 h-28 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                {banner.image_url ? (
                  <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{banner.title}</h3>
                    {banner.subtitle && <p className="text-sm text-gray-500">{banner.subtitle}</p>}
                  </div>
                  {getStatusBadge(banner.status)}
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span>Position: {BANNER_POSITIONS.find(p => p.value === banner.position)?.label || banner.position}</span>
                  <span>Type: {BANNER_TYPES.find(t => t.value === banner.banner_type)?.label || banner.banner_type}</span>
                  <span>Priority: {banner.priority}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {renderWorkflowActions('banners', banner)}
                  <button
                    onClick={() => openEditModal('banner', banner)}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {banner.status !== 'archived' && (
                    <button
                      onClick={() => handleArchive('banner', banner.id)}
                      className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center gap-1"
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Helper function to get sale status
  const getSaleStatus = (sale) => {
    const now = new Date();
    const start = new Date(sale.start_time);
    const end = new Date(sale.end_time);
    
    if (!sale.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-600' };
    if (now < start) return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' };
    if (now > end) return { label: 'Ended', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Live', color: 'bg-green-100 text-green-700' };
  };

  // Format datetime for display
  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Render Flash Sales
  const renderFlashSales = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Flash Sales</h2>
          <p className="text-gray-500 text-sm">Time-limited sales and discounts</p>
        </div>
        <button
          onClick={() => openAddModal('flash-sale')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-flash-sale-btn"
        >
          <Plus className="w-4 h-4" />
          Add Flash Sale
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">Flash Sale Tips</h4>
            <ul className="text-sm text-amber-700 mt-1 space-y-1">
              <li>• Set a start and end time for time-sensitive sales</li>
              <li>• Choose percentage or fixed discount type</li>
              <li>• Sales automatically appear on Buy Mobile/Laptop pages when active</li>
              <li>• Only one flash sale can be active at a time per page</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {flashSales.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No flash sales yet</h3>
            <p className="text-gray-500 mt-1">Create time-limited sales to boost conversions</p>
          </div>
        ) : (
          flashSales.map(sale => {
            const status = getSaleStatus(sale);
            return (
              <div key={sale.id} className="bg-white rounded-xl border p-5" data-testid={`flash-sale-item-${sale.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{sale.name}</h3>
                      {sale.description && <p className="text-sm text-gray-500">{sale.description}</p>}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block">Discount</span>
                    <span className="font-medium">
                      {sale.discount_type === 'percentage' 
                        ? `${sale.discount_value}% OFF` 
                        : `₹${sale.discount_value} OFF`}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Page</span>
                    <span className="font-medium capitalize">{sale.page_slug?.replace('-', ' ') || 'All'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> Starts
                    </span>
                    <span className="font-medium">{formatDateTime(sale.start_time)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block flex items-center gap-1">
                      <Timer className="w-3.5 h-3.5" /> Ends
                    </span>
                    <span className="font-medium">{formatDateTime(sale.end_time)}</span>
                  </div>
                </div>

                {sale.category && (
                  <div className="mt-3">
                    <span className="text-xs text-gray-500">Category: </span>
                    <span className="text-xs font-medium text-primary capitalize">{sale.category}</span>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {sale.is_active ? (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> Active
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <EyeOff className="w-3.5 h-3.5" /> Inactive
                      </span>
                    )}
                    {sale.max_discount && (
                      <span className="text-xs text-gray-500">Max: ₹{sale.max_discount}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {renderWorkflowActions('flash-sales', sale)}
                    <button
                      onClick={() => openEditModal('flash-sale', sale)}
                      className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    {sale.status !== 'archived' && (
                      <button
                        onClick={() => handleArchive('flash-sale', sale.id)}
                        className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center gap-1"
                      >
                        <Archive className="w-3.5 h-3.5" /> Archive
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // Render Testimonials
  const renderTestimonials = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Testimonials</h2>
          <p className="text-gray-500 text-sm">Customer reviews and feedback</p>
        </div>
        <button
          onClick={() => openAddModal('testimonial')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {testimonials.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border p-12 text-center">
            <MessageSquareQuote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No testimonials yet</h3>
            <p className="text-gray-500 mt-1">Add customer reviews to build trust</p>
          </div>
        ) : (
          testimonials.map(testimonial => (
            <div key={testimonial.id} className="bg-white rounded-xl border p-5" data-testid={`testimonial-item-${testimonial.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden">
                    {testimonial.customer_image_url ? (
                      <img src={testimonial.customer_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-semibold">
                        {testimonial.customer_name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{testimonial.customer_name}</h4>
                    <p className="text-xs text-gray-500">{testimonial.customer_location}</p>
                  </div>
                </div>
                {getStatusBadge(testimonial.status)}
              </div>
              
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star 
                    key={star} 
                    className={`w-4 h-4 ${star <= testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                  />
                ))}
              </div>
              
              <p className="text-sm text-gray-600 line-clamp-3">{testimonial.review_text}</p>
              
              {testimonial.product_purchased && (
                <p className="text-xs text-gray-400 mt-2">Purchased: {testimonial.product_purchased}</p>
              )}
              
              <div className="mt-4 pt-3 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {testimonial.is_featured && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Featured</span>
                  )}
                  {testimonial.is_verified_purchase && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1">
                      <Check className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {renderWorkflowActions('testimonials', testimonial)}
                  <button
                    onClick={() => openEditModal('testimonial', testimonial)}
                    className="p-1.5 hover:bg-gray-100 rounded"
                  >
                    <Pencil className="w-4 h-4 text-gray-500" />
                  </button>
                  {testimonial.status !== 'archived' && (
                    <button
                      onClick={() => handleArchive('testimonial', testimonial.id)}
                      className="p-1.5 hover:bg-red-50 rounded"
                    >
                      <Archive className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Render News
  const renderNews = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">News & Press</h2>
          <p className="text-gray-500 text-sm">Press releases and media coverage</p>
        </div>
        <button
          onClick={() => openAddModal('news')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-news-btn"
        >
          <Plus className="w-4 h-4" />
          Add Article
        </button>
      </div>

      <div className="grid gap-4">
        {news.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No articles yet</h3>
            <p className="text-gray-500 mt-1">Add news articles and press releases</p>
          </div>
        ) : (
          news.map(article => (
            <div key={article.id} className="bg-white rounded-xl border p-4 flex gap-4" data-testid={`news-item-${article.id}`}>
              <div className="w-40 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                {article.featured_image_url ? (
                  <img src={article.featured_image_url} alt={article.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Newspaper className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 line-clamp-1">{article.title}</h3>
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{article.excerpt || 'No excerpt'}</p>
                  </div>
                  {getStatusBadge(article.status)}
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span className="px-2 py-0.5 bg-gray-100 rounded">
                    {NEWS_CATEGORIES.find(c => c.value === article.category)?.label || article.category}
                  </span>
                  {article.source_name && <span>Source: {article.source_name}</span>}
                  {article.is_featured && <span className="text-yellow-600">Featured</span>}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {renderWorkflowActions('news', article)}
                  <button
                    onClick={() => openEditModal('news', article)}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {article.status !== 'archived' && (
                    <button
                      onClick={() => handleArchive('news', article.id)}
                      className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center gap-1"
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Render Categories
  const renderCategories = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Categories</h2>
          <p className="text-gray-500 text-sm">Website navigation categories</p>
        </div>
        <button
          onClick={() => openAddModal('category')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border p-12 text-center">
            <FolderTree className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No categories yet</h3>
            <p className="text-gray-500 mt-1">Create categories for website navigation</p>
          </div>
        ) : (
          categories.map(category => (
            <div key={category.id} className="bg-white rounded-xl border p-5" data-testid={`category-item-${category.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {category.image_url ? (
                    <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderTree className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{category.name}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">/{category.slug}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${category.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {category.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {category.is_featured && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Featured</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t flex items-center justify-between">
                <span className="text-xs text-gray-500">Order: {category.display_order}</span>
                <div className="flex items-center gap-1">
                  {renderWorkflowActions('categories', category)}
                  <button
                    onClick={() => openEditModal('category', category)}
                    className="p-1.5 hover:bg-gray-100 rounded"
                  >
                    <Pencil className="w-4 h-4 text-gray-500" />
                  </button>
                  {category.status !== 'archived' && (
                    <button
                      onClick={() => handleArchive('category', category.id)}
                      className="p-1.5 hover:bg-red-50 rounded"
                    >
                      <Archive className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderHomepage = () => {
    const configured = pageContent
      .filter(item => item.page_slug === 'home' && item.metadata?.kind === 'homepage_section')
      .sort((left, right) => (left.display_order || 0) - (right.display_order || 0));
    const configuredById = new Map(configured.map(item => [item.metadata?.section_id, item]));
    const sections = HOMEPAGE_SECTIONS
      .map(definition => ({ definition, item: configuredById.get(definition.id) }))
      .sort((left, right) => (
        (left.item?.display_order ?? left.definition.order)
        - (right.item?.display_order ?? right.definition.order)
      ));
    const heroBanners = banners.filter(banner => banner.position === 'home_top' && banner.status !== 'archived');

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Homepage Composer</h2>
            <p className="text-gray-500 text-sm mt-1">Control the public homepage without changing website code.</p>
          </div>
          <button
            type="button"
            onClick={handleHomepagePreview}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
          >
            <Eye className="w-4 h-4" /> Preview Unpublished Draft
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
          Saving creates a draft. The live public snapshot changes only after checker approval. The production one-page site is not connected to this workspace.
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-900">Hero banners</h3>
              <p className="text-sm text-gray-500">Separate desktop/mobile artwork, CTA, schedule and priority.</p>
            </div>
            <button
              type="button"
              onClick={() => setActiveTab('banners')}
              className="px-3 py-2 bg-primary text-white rounded-lg text-sm"
            >
              Manage Banners
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">{heroBanners.length} configured</span>
            <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm">{heroBanners.filter(item => item.status === 'published').length} published</span>
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm">{heroBanners.filter(item => item.approval_status === 'pending_approval').length} awaiting approval</span>
          </div>
        </div>

        <div className="space-y-3">
          {sections.map(({ definition, item }, index) => {
            const isEnabled = item ? (item.metadata?.enabled ?? item.is_active) : definition.enabled;
            const configuredIndex = item ? configured.findIndex(entry => entry.id === item.id) : -1;
            return (
              <div key={definition.id} className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center font-semibold text-slate-600">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{definition.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs ${item ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item ? 'Configured' : 'Not configured'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${isEnabled ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {isEnabled ? 'Visible after approval' : 'Hidden'}
                    </span>
                    {item ? getStatusBadge(item.status) : null}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{item?.title || definition.title}</p>
                  {item ? <div className="mt-3">{renderWorkflowActions('page-content', item)}</div> : null}
                </div>
                <div className="flex items-center gap-2">
                  {item ? (
                    <>
                      <button type="button" disabled={configuredIndex <= 0} onClick={() => handleHomepageMove(configured, configuredIndex, -1)} className="px-2.5 py-2 border rounded-lg disabled:opacity-30" aria-label={`Move ${definition.name} up`}>↑</button>
                      <button type="button" disabled={configuredIndex < 0 || configuredIndex >= configured.length - 1} onClick={() => handleHomepageMove(configured, configuredIndex, 1)} className="px-2.5 py-2 border rounded-lg disabled:opacity-30" aria-label={`Move ${definition.name} down`}>↓</button>
                      <button type="button" onClick={() => handleHomepageVisibility(item)} className="px-3 py-2 border rounded-lg text-sm">
                        {isEnabled ? 'Hide draft' : 'Show draft'}
                      </button>
                    </>
                  ) : null}
                  <button type="button" onClick={() => openHomepageSection(definition, item)} className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1">
                    <Pencil className="w-3.5 h-3.5" /> {item ? 'Edit' : 'Configure'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Page Content
  const renderPageContent = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Page Content</h2>
          <p className="text-gray-500 text-sm">USPs, Features, FAQs, and other page sections</p>
        </div>
        <button
          onClick={() => openAddModal('page-content')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-page-content-btn"
        >
          <Plus className="w-4 h-4" />
          Add Content
        </button>
      </div>

      <div className="grid gap-4">
        {pageContent.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No page content yet</h3>
            <p className="text-gray-500 mt-1">Add USPs, features, FAQs and more</p>
          </div>
        ) : (
          pageContent.map(content => (
            <div key={content.id} className="bg-white rounded-xl border p-4 flex gap-4" data-testid={`page-content-item-${content.id}`}>
              <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                {content.image_url ? (
                  <img src={content.image_url} alt={content.title} className="w-full h-full object-cover" />
                ) : content.icon_name ? (
                  <span className="text-2xl text-primary">{content.icon_name}</span>
                ) : (
                  <Layers className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{content.title}</h3>
                    {content.subtitle && <p className="text-sm text-gray-500">{content.subtitle}</p>}
                    {content.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{content.description}</p>}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${content.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {content.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                    {PAGE_SLUGS.find(p => p.value === content.page_slug)?.label || content.page_slug}
                  </span>
                  <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded">
                    {PAGE_SECTION_TYPES.find(t => t.value === content.section_type)?.label || content.section_type}
                  </span>
                  <span>Order: {content.display_order}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {renderWorkflowActions('page-content', content)}
                  <button
                    onClick={() => openEditModal('page-content', content)}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {content.status !== 'archived' && (
                    <button
                      onClick={() => handleArchive('page-content', content.id)}
                      className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center gap-1"
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Render Static Pages
  const renderStaticPages = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Static Pages</h2>
          <p className="text-gray-500 text-sm">Custom pages with full content control</p>
        </div>
        <button
          onClick={() => openAddModal('static-page')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          data-testid="add-static-page-btn"
        >
          <Plus className="w-4 h-4" />
          Add Page
        </button>
      </div>

      <div className="grid gap-4">
        {staticPages.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No static pages yet</h3>
            <p className="text-gray-500 mt-1">Create custom pages with full content control</p>
          </div>
        ) : (
          staticPages.map(page => (
            <div key={page.id} className="bg-white rounded-xl border p-4 flex justify-between items-center" data-testid={`static-page-item-${page.id}`}>
              <div>
                <h3 className="font-semibold text-gray-900">{page.title}</h3>
                <p className="text-sm text-gray-500">/{page.slug}</p>
                {page.meta_description && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-1">{page.meta_description}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(page.status)}
                {renderWorkflowActions('static-pages', page)}
                <button
                  onClick={() => openEditModal('static-page', page)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Pencil className="w-4 h-4 text-gray-500" />
                </button>
                {page.status !== 'archived' && (
                  <button
                    onClick={() => handleArchive('static-page', page.id)}
                    className="p-2 hover:bg-red-50 rounded-lg"
                  >
                    <Archive className="w-4 h-4 text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const updateSiteList = (field, updater) => {
    const current = Array.isArray(siteSettings[field]) ? siteSettings[field] : [];
    setSiteSettings({ ...siteSettings, [field]: updater(current) });
  };

  const renderNavigationEditor = (field, title, description) => {
    const items = Array.isArray(siteSettings[field]) ? siteSettings[field] : [];
    const updateItem = (index, changes) => updateSiteList(field, current => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...changes } : item
    )));
    const moveItem = (index, direction) => updateSiteList(field, current => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((item, itemIndex) => ({ ...item, order: (itemIndex + 1) * 10 }));
    });

    return (
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        {items.map((item, index) => (
          <div key={`${field}-${index}`} className="grid md:grid-cols-[1fr_1.4fr_auto] gap-3 items-center bg-slate-50 rounded-lg p-3">
            <input value={item.label || ''} onChange={(event) => updateItem(index, { label: event.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Label" />
            <input value={item.href || ''} onChange={(event) => updateItem(index, { href: event.target.value })} className="px-3 py-2 border rounded-lg" placeholder="/destination" />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={item.visible !== false} onChange={(event) => updateItem(index, { visible: event.target.checked })} /> Visible</label>
              <button type="button" disabled={index === 0} onClick={() => moveItem(index, -1)} className="px-2 py-1 border rounded disabled:opacity-30">↑</button>
              <button type="button" disabled={index === items.length - 1} onClick={() => moveItem(index, 1)} className="px-2 py-1 border rounded disabled:opacity-30">↓</button>
              <button type="button" onClick={() => updateSiteList(field, current => current.filter((_, itemIndex) => itemIndex !== index))} className="px-2 py-1 text-red-600">Remove</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => updateSiteList(field, current => [...current, { label: '', href: '/', visible: true, order: (current.length + 1) * 10 }])} className="px-3 py-2 border border-primary text-primary rounded-lg text-sm">
          + Add navigation item
        </button>
      </div>
    );
  };

  const renderFooterEditor = () => {
    const groups = Array.isArray(siteSettings.footer_groups) ? siteSettings.footer_groups : [];
    const updateGroups = updater => setSiteSettings({ ...siteSettings, footer_groups: updater(groups) });
    const updateGroup = (groupIndex, changes) => updateGroups(current => current.map((group, index) => index === groupIndex ? { ...group, ...changes } : group));
    const updateLink = (groupIndex, linkIndex, changes) => updateGroups(current => current.map((group, index) => index === groupIndex ? {
      ...group,
      links: (group.links || []).map((link, position) => position === linkIndex ? { ...link, ...changes } : link),
    } : group));

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Footer Navigation</h3>
          <p className="text-sm text-gray-500">Manage the public footer groups without developer changes.</p>
        </div>
        {groups.map((group, groupIndex) => (
          <div key={`footer-group-${groupIndex}`} className="bg-white border rounded-xl p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <input value={group.title || ''} onChange={(event) => updateGroup(groupIndex, { title: event.target.value })} className="flex-1 min-w-[220px] px-3 py-2 border rounded-lg font-semibold" placeholder="Group title" />
              <button type="button" onClick={() => updateGroups(current => current.filter((_, index) => index !== groupIndex))} className="text-sm text-red-600">Remove group</button>
            </div>
            {(group.links || []).map((link, linkIndex) => (
              <div key={`footer-link-${groupIndex}-${linkIndex}`} className="grid md:grid-cols-[1fr_1.4fr_auto] gap-3 items-center bg-slate-50 rounded-lg p-3">
                <input value={link.label || ''} onChange={(event) => updateLink(groupIndex, linkIndex, { label: event.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Label" />
                <input value={link.href || ''} onChange={(event) => updateLink(groupIndex, linkIndex, { href: event.target.value })} className="px-3 py-2 border rounded-lg" placeholder="/destination" />
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={link.visible !== false} onChange={(event) => updateLink(groupIndex, linkIndex, { visible: event.target.checked })} /> Visible</label>
                  <button type="button" onClick={() => updateGroup(groupIndex, { links: (group.links || []).filter((_, index) => index !== linkIndex) })} className="text-xs text-red-600">Remove</button>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => updateGroup(groupIndex, { links: [...(group.links || []), { label: '', href: '/', visible: true, order: ((group.links || []).length + 1) * 10 }] })} className="px-3 py-2 border border-primary text-primary rounded-lg text-sm">+ Add footer link</button>
          </div>
        ))}
        <button type="button" onClick={() => updateGroups(current => [...current, { title: 'New group', order: (current.length + 1) * 10, links: [] }])} className="px-3 py-2 border border-primary text-primary rounded-lg text-sm">+ Add footer group</button>
      </div>
    );
  };

  // Render Site Settings
  const renderSiteSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Site Settings</h2>
          <p className="text-gray-500 text-sm">Global website configuration</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {renderWorkflowActions('site-settings', { ...siteSettings, id: 'site_settings', status: siteSettings.status || 'draft' })}
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-6">
        {/* Logo Section - Prominent */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-2 text-blue-900">Site Logo</h3>
          <p className="text-sm text-blue-700 mb-4">
            Recommended size: <strong>200 x 60 px</strong> (PNG with transparent background preferred)
          </p>
          <div className="max-w-md">
            <ImageUploader
              category="logos"
              value={siteSettings.logo_url || ''}
              onChange={(url) => setSiteSettings({ ...siteSettings, logo_url: url })}
              label="Upload Logo"
            />
          </div>
        </div>

        {/* Branding */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Branding & Identity</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tagline / Slogan</label>
              <input
                type="text"
                value={siteSettings.tagline || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, tagline: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="खुशियों वाला BOX"
              />
              <p className="text-xs text-gray-500 mt-1">Displayed in footer below the logo</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Favicon URL</label>
              <input
                type="url"
                value={siteSettings.favicon_url || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, favicon_url: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">Browser tab icon (32x32 px recommended)</p>
            </div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-2 text-green-900">SEO Settings</h3>
          <p className="text-sm text-green-700 mb-4">
            Configure meta tags for better search engine visibility
          </p>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Title (Meta Title)</label>
              <input
                type="text"
                value={siteSettings.meta_title || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, meta_title: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="ZOBOX - Buy Refurbished Phones & Laptops | Best Prices"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Description (Meta Description)</label>
              <textarea
                value={siteSettings.meta_description || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, meta_description: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
                placeholder="Buy certified refurbished phones and laptops at best prices. 6 months warranty, COD available, free shipping across India."
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Keywords</label>
              <input
                type="text"
                value={siteSettings.meta_keywords || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, meta_keywords: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="refurbished phones, second hand mobile, used laptops, zobox"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated keywords</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OG Image (Social Share Image)</label>
              <input
                type="url"
                value={siteSettings.og_image || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, og_image: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 1200 x 630 px (shown when sharing on social media)</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Public Contact Desks</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">B2C customer desk</label>
              <input
                type="text"
                value={siteSettings.b2c_phone || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, b2c_phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="918130808875"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">B2R partner desk</label>
              <input
                type="text"
                value={siteSettings.b2r_phone || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, b2r_phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="919999929858"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Support hours</label>
              <input
                type="text"
                value={siteSettings.support_hours || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, support_hours: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="10:30 AM–6:30 PM, Monday–Saturday"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Support email</label>
              <input type="email" value={siteSettings.email || ''} onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="support@zobox.in" />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Navigation</h3>
            <p className="text-sm text-gray-500">Labels, destinations, order and visibility become public only after approval.</p>
          </div>
          {renderNavigationEditor('navigation_primary', 'Primary navigation', 'Shown directly in the desktop and mobile header.')}
          {renderNavigationEditor('navigation_more', 'More menu', 'Additional public routes shown under More.')}
        </div>

        {/* Addresses */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Addresses</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registered Address</label>
              <textarea
                value={siteSettings.address_registered || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, address_registered: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Corporate Address</label>
              <textarea
                value={siteSettings.address_corporate || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, address_corporate: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
              <input
                type="url"
                value={siteSettings.social_facebook || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, social_facebook: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
              <input
                type="url"
                value={siteSettings.social_twitter || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, social_twitter: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
              <input
                type="url"
                value={siteSettings.social_instagram || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, social_instagram: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <input
                type="url"
                value={siteSettings.social_linkedin || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, social_linkedin: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
              <input
                type="url"
                value={siteSettings.social_youtube || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, social_youtube: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {renderFooterEditor()}

        {/* Footer Content */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Footer Content</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Disclaimer Text</label>
              <textarea
                value={siteSettings.footer_disclaimer || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, footer_disclaimer: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Security Notice</label>
              <textarea
                value={siteSettings.security_notice || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, security_notice: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Analytics & Tracking Pixels */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-2 text-purple-900">Analytics & Tracking Pixels</h3>
          <p className="text-sm text-purple-700 mb-4">
            Configure tracking codes for analytics and marketing pixels
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Analytics 4 (GA4)</label>
              <input
                type="text"
                value={siteSettings.google_analytics_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, google_analytics_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="G-XXXXXXXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">Measurement ID from GA4</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Tag Manager</label>
              <input
                type="text"
                value={siteSettings.google_tag_manager_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, google_tag_manager_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="GTM-XXXXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">Container ID from GTM</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook Pixel</label>
              <input
                type="text"
                value={siteSettings.facebook_pixel_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, facebook_pixel_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="XXXXXXXXXXXXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">15-digit Pixel ID from Meta Business</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads</label>
              <input
                type="text"
                value={siteSettings.google_ads_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, google_ads_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="AW-XXXXXXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">Google Ads conversion ID</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Insight Tag</label>
              <input
                type="text"
                value={siteSettings.linkedin_partner_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, linkedin_partner_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="XXXXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">Partner ID from LinkedIn Campaign Manager</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Channel ID</label>
              <input
                type="text"
                value={siteSettings.youtube_channel_id || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, youtube_channel_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="UCxxxxxxxxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">For YouTube analytics & embedding</p>
            </div>
          </div>
        </div>

        {/* Custom Scripts */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-2 text-orange-900">Custom Scripts</h3>
          <p className="text-sm text-orange-700 mb-4">
            Add custom tracking scripts (injected into page head/body)
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Head Scripts</label>
              <textarea
                value={siteSettings.custom_head_scripts || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, custom_head_scripts: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                rows={4}
                placeholder="<!-- Scripts to inject in <head> -->"
              />
              <p className="text-xs text-gray-500 mt-1">Will be injected before &lt;/head&gt;</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Body Scripts</label>
              <textarea
                value={siteSettings.custom_body_scripts || ''}
                onChange={(e) => setSiteSettings({ ...siteSettings, custom_body_scripts: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                rows={4}
                placeholder="<!-- Scripts to inject in <body> -->"
              />
              <p className="text-xs text-gray-500 mt-1">Will be injected after &lt;body&gt;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Modal
  const renderModal = () => {
    if (!showModal) return null;

    const titles = {
      banner: editItem ? 'Edit Banner' : 'Add Banner',
      'flash-sale': editItem ? 'Edit Flash Sale' : 'Add Flash Sale',
      testimonial: editItem ? 'Edit Testimonial' : 'Add Testimonial',
      news: editItem ? 'Edit Article' : 'Add Article',
      category: editItem ? 'Edit Category' : 'Add Category',
      'page-content': editItem ? 'Edit Page Content' : 'Add Page Content',
      'static-page': editItem ? 'Edit Static Page' : 'Add Static Page'
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-xl font-semibold">{titles[modalType]}</h2>
            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-4">
              {/* Banner Form */}
              {modalType === 'banner' && (
                <>
                  {/* Banner Size Guidelines */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-900 mb-2">Recommended Banner Sizes</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• <strong>Hero Slider:</strong> 1920 x 600 px (Desktop) / 768 x 400 px (Mobile)</li>
                      <li>• <strong>Promotional:</strong> 1200 x 400 px</li>
                      <li>• <strong>Category Banner:</strong> 800 x 300 px</li>
                      <li>• <strong>Sidebar:</strong> 300 x 600 px</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">Use JPG/PNG format. Max file size: 10MB</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle || ''}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <ImageUploader
                    category="banners"
                    value={formData.image_url || ''}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Banner Image *"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                      <select
                        value={formData.position || 'home_top'}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {BANNER_POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={formData.banner_type || 'hero'}
                        onChange={(e) => setFormData({ ...formData, banner_type: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {BANNER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
                      <input
                        type="text"
                        value={formData.link_url || ''}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="/buyMobile"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <input
                        type="number"
                        value={formData.priority || 0}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {CONTENT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* Flash Sale Form */}
              {modalType === 'flash-sale' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sale Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., Weekend Flash Sale"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={2}
                      placeholder="Brief description of the sale"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                      <input
                        type="datetime-local"
                        value={formData.start_time || ''}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                      <input
                        type="datetime-local"
                        value={formData.end_time || ''}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                      <select
                        value={formData.discount_type || 'percentage'}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (₹)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value *</label>
                      <input
                        type="number"
                        value={formData.discount_value || ''}
                        onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={formData.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                      <input
                        type="number"
                        value={formData.max_discount || ''}
                        onChange={(e) => setFormData({ ...formData, max_discount: parseFloat(e.target.value) || null })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Optional cap"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Show on Page</label>
                      <select
                        value={formData.page_slug || 'buy-mobile'}
                        onChange={(e) => setFormData({ ...formData, page_slug: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="buy-mobile">Buy Mobile Page</option>
                        <option value="buy-laptop">Buy Laptop Page</option>
                        <option value="all">All Pages</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product Category</label>
                      <select
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value || null })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">All Products</option>
                        <option value="mobile">Mobile Phones</option>
                        <option value="laptop">Laptops</option>
                      </select>
                    </div>
                  </div>
                  <ImageUploader
                    category="banners"
                    value={formData.banner_image_url || ''}
                    onChange={(url) => setFormData({ ...formData, banner_image_url: url })}
                    label="Sale Banner Image (Optional)"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="flash-sale-active"
                      checked={formData.is_active !== false}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="flash-sale-active" className="text-sm text-gray-700">
                      Sale is active (will show when within time window)
                    </label>
                  </div>
                </>
              )}

              {/* Testimonial Form */}
              {modalType === 'testimonial' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                      <input
                        type="text"
                        value={formData.customer_name || ''}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                      <input
                        type="text"
                        value={formData.customer_location || ''}
                        onChange={(e) => setFormData({ ...formData, customer_location: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Delhi"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className="p-1"
                        >
                          <Star 
                            className={`w-8 h-8 ${star <= (formData.rating || 5) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Text *</label>
                    <textarea
                      value={formData.review_text || ''}
                      onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={4}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Purchased</label>
                    <input
                      type="text"
                      value={formData.product_purchased || ''}
                      onChange={(e) => setFormData({ ...formData, product_purchased: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="iPhone 13 Pro"
                    />
                  </div>
                  <ImageUploader
                    category="testimonials"
                    value={formData.customer_image_url || ''}
                    onChange={(url) => setFormData({ ...formData, customer_image_url: url })}
                    label="Customer Photo"
                  />
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_verified_purchase || false}
                        onChange={(e) => setFormData({ ...formData, is_verified_purchase: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Verified Purchase</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_featured || false}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {CONTENT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* News Form */}
              {modalType === 'news' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                    <input
                      type="text"
                      value={formData.excerpt || ''}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Short description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                    <textarea
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={6}
                      required
                    />
                  </div>
                  <ImageUploader
                    category="news"
                    value={formData.featured_image_url || ''}
                    onChange={(url) => setFormData({ ...formData, featured_image_url: url })}
                    label="Featured Image"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={formData.category || 'press'}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {NEWS_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                      <input
                        type="text"
                        value={formData.source_name || ''}
                        onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Economic Times"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_featured || false}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {CONTENT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* Category Form */}
              {modalType === 'category' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                      <input
                        type="text"
                        value={formData.slug || ''}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="mobile-phones"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <ImageUploader
                    category="categories"
                    value={formData.image_url || ''}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Category Image"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order || 0}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active !== false}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_featured || false}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Featured on Homepage</span>
                    </label>
                  </div>
                </>
              )}

              {/* Page Content Form */}
              {modalType === 'page-content' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Page *</label>
                      <select
                        value={formData.page_slug || 'home'}
                        onChange={(e) => setFormData({ ...formData, page_slug: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {PAGE_SLUGS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section Type *</label>
                      <select
                        value={formData.section_type || 'usp'}
                        onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {PAGE_SECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle || ''}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  {formData.metadata?.kind === 'homepage_section' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900">
                      Homepage module: <strong>{formData.metadata.section_id}</strong>. Publication remains subject to checker approval.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Label</label>
                      <input
                        type="text"
                        value={formData.link_text || ''}
                        onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Explore"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Destination</label>
                      <input
                        type="text"
                        value={formData.link_url || ''}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="/buy"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon Name</label>
                      <input
                        type="text"
                        value={formData.icon_name || ''}
                        onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="shield-check"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                      <input
                        type="number"
                        value={formData.display_order || 0}
                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <ImageUploader
                    category="pages"
                    value={formData.image_url || ''}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Section Image"
                  />
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active !== false}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </>
              )}

              {/* Static Page Form */}
              {modalType === 'static-page' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                      <input
                        type="text"
                        value={formData.slug || ''}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="about-us"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                    <input
                      type="text"
                      value={formData.meta_title || ''}
                      onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                    <textarea
                      value={formData.meta_description || ''}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                    <textarea
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                      rows={10}
                      required
                      placeholder="HTML or Markdown content..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {CONTENT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {renderScheduleFields()}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editItem ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">B2C Website CMS</h1>
          <p className="text-gray-500 mt-1">Manage content for zobox.in website</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="flex overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading && !['dashboard', 'settings'].includes(activeTab) ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'homepage' && renderHomepage()}
            {activeTab === 'logo-bank' && renderLogoBank()}
            {activeTab === 'banners' && renderBanners()}
            {activeTab === 'flash-sales' && renderFlashSales()}
            {activeTab === 'testimonials' && renderTestimonials()}
            {activeTab === 'news' && renderNews()}
            {activeTab === 'categories' && renderCategories()}
            {activeTab === 'page-content' && renderPageContent()}
            {activeTab === 'static-pages' && renderStaticPages()}
            {activeTab === 'settings' && renderSiteSettings()}
            {activeTab === 'approvals' && renderApprovals()}
            {activeTab === 'audit' && renderAuditLogs()}
          </>
        )}

        {/* Modal */}
        {renderModal()}
        {renderVersionsModal()}
      </div>
    </Layout>
  );
}

export default B2CCMS;
